import { type ReactElement, useState } from 'react';
import { Button, EmptyState, Notice, PageHeader, Panel, Stack } from '@vello/web-theme';
import { pendingAdjustments, riderWalletAuthority, screenAllowed } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { CardBody, useCardRead } from '../components/person-card';
import { RiderWalletPendingItem } from '../components/rider-wallet-pending';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';

/**
 * Finance › Saldo dos passageiros (D737b, ADR-0150, doc 32): the market's inbox of rider-balance adjustments waiting for a SECOND operator
 * (`GET /v1/ops/rider-wallet/adjustments?market=&status=PENDING_APPROVAL`, `rider_wallet:approve`). The market is the header's scope - no second picker.
 * Each row says what the adjustment does, for whom, who proposed it and why; approve or turn it down (a reason is asked) unless the operator proposed it
 * (the screen says so; the server refuses `SELF_APPROVAL` anyway) or it is about their own account. A refused or failed read is an error, never an empty list.
 * The same item is drawn on the passenger record's "Saldo Vello" card.
 */
export function RiderWalletApprovalsScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { market, choices } = useScopeMarket();
  const { mayApprove } = riderWalletAuthority(grants);
  const { state, reload } = useCardRead(mayApprove && market !== undefined ? () => runtime.riderWallet.inbox(market) : undefined, [runtime, market, mayApprove]);
  const [result, setResult] = useState<string | undefined>(undefined);
  const mayOpenRider = screenAllowed(grants, 'passenger_detail');

  return (
    <>
      <PageHeader title={t('rwallet.inbox.title')} subtitle={t('rwallet.inbox.subtitle')} />
      {!mayApprove ? (
        <Panel>
          <EmptyState title={t('rwallet.inbox.noPermission')} />
        </Panel>
      ) : market === undefined ? (
        <ScopeMarketNotice choices={choices} />
      ) : (
        <Panel>
          {result !== undefined && (
            <div data-testid="rwallet-inbox-result"><Notice tone="success">{result}</Notice></div>
          )}
          <CardBody
            what={t('rwallet.what')}
            state={state}
            reload={reload}
            isEmpty={(r) => pendingAdjustments(r.adjustments).length === 0}
            empty={t('rwallet.inbox.empty')}
          >
            {(r) => (
              <Stack gap={4}>
                {pendingAdjustments(r.adjustments).map((a) => (
                  <Stack key={a.adjustmentId} gap={2}>
                    <RiderWalletPendingItem
                      adjustment={a}
                      mayApprove={mayApprove}
                      showRider
                      onDecided={(outcome) => {
                        if (outcome === 'APPROVED') setResult(t('rwallet.result.approved'));
                        else if (outcome === 'REJECTED') setResult(t('rwallet.result.rejected'));
                        reload();
                      }}
                    />
                    {mayOpenRider && (
                      <div>
                        <Button variant="ghost" size="sm" onClick={() => navigate('passenger_detail', { id: a.riderId })}>
                          {t('rwallet.inbox.open')}
                        </Button>
                      </div>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </CardBody>
        </Panel>
      )}
    </>
  );
}
